import React, { useState, useEffect } from 'react';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import DashboardFooter from '../../layouts/dashboard_footer';
import Canvas from '../../layouts/dahboard_canvas';
import { useBaseUrl } from '../../../utils/utils';
import localForage from 'localforage';

type RITab = "collaboration" | "referrals" | "financial";

interface PartnerStat { name: string; referrals: number; referrals_in: number; referrals_out: number; }

const ReportsInsights: React.FC = () => {
    const [activeTab, setActiveTab] = useState<RITab>('collaboration');
    const [partners, setPartners] = useState<PartnerStat[]>([]);
    const [loading, setLoading] = useState(true);
    const baseUrl = useBaseUrl();

    useEffect(() => { document.title = "Uzima - Reports & Insights"; loadData(); }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const clinicId = await localForage.getItem('clinic_id');
            if (!clinicId) { setLoading(false); return; }
            const res = await fetch(`${baseUrl}facilities/partner-network?clinic_id=${clinicId}`);
            if (res.ok) {
                const d = await res.json();
                if (d.status === 'success') {
                    setPartners((d.data || []).map((p: any) => ({
                        name: p.name, referrals: p.referrals_in + p.referrals_out,
                        referrals_in: p.referrals_in, referrals_out: p.referrals_out,
                    })));
                }
            }
        } catch (err) { console.error("Load error:", err); }
        finally { setLoading(false); }
    };

    const exportCsv = () => {
        const rows = [['Partner', 'Referrals In', 'Referrals Out', 'Total'], ...partners.map(p => [p.name, String(p.referrals_in), String(p.referrals_out), String(p.referrals)])];
        const csv = rows.map(r => r.map(cell => `"${cell.replace(/"/g, '""')}"`).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `partner-collaboration-report-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const tabs: Array<{ key: RITab; label: string; icon: string }> = [
        { key: "collaboration", label: "Collaboration", icon: "bx bx-group" },
        { key: "referrals", label: "Referral Analytics", icon: "bx bx-transfer" },
        { key: "financial", label: "Financial", icon: "bx bx-dollar-circle" },
    ];

    const totalReferrals = partners.reduce((s, p) => s + p.referrals, 0);

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    <div className="sc-wrap">
                        <div className="sc-header">
                            <div><h1>Reports & Insights</h1><p>Collaboration reports, referral analytics, and financial summaries</p></div>
                            <button className="sc-btn sc-btn-outline" onClick={exportCsv} disabled={partners.length === 0}>
                                <i className="bx bx-download"></i> Export CSV
                            </button>
                        </div>
                        <div className="sc-stats">
                            <div className="sc-stat"><div className="sc-stat-icon teal"><i className="bx bx-group"></i></div><div className="sc-stat-value">{partners.length}</div><div className="sc-stat-label">Partners</div></div>
                            <div className="sc-stat"><div className="sc-stat-icon violet"><i className="bx bx-transfer"></i></div><div className="sc-stat-value">{totalReferrals}</div><div className="sc-stat-label">Total Referrals</div></div>
                        </div>
                        <div className="sc-tabs">{tabs.map(t => (<button key={t.key} className={`sc-tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => setActiveTab(t.key)}><i className={t.icon}></i> {t.label}</button>))}</div>
                        {loading ? <div className="sc-spinner"></div> : (
                            <>
                                {activeTab === 'collaboration' && (
                                    <div className="sc-panel"><div className="sc-panel-head"><h3>Collaboration Reports</h3></div>
                                        <div className="sc-panel-body">
                                            {partners.length === 0 ? (
                                                <div className="sc-empty"><i className="bx bx-file"></i><h3>No Partner Activity Yet</h3><p>Collaboration reports populate once referrals are exchanged with other facilities.</p></div>
                                            ) : (
                                                <div className="sc-table-wrap"><table className="sc-table"><thead><tr><th>Partner</th><th>Referred In</th><th>Referred Out</th><th>Total</th></tr></thead>
                                                    <tbody>{partners.map((p, i) => (<tr key={i}><td><strong>{p.name}</strong></td><td>{p.referrals_in}</td><td>{p.referrals_out}</td><td>{p.referrals}</td></tr>))}</tbody></table></div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {activeTab === 'referrals' && (
                                    <div className="sc-panel"><div className="sc-panel-body"><div className="sc-empty"><i className="bx bx-transfer"></i><h3>Referral Analytics</h3><p>Detailed referral analytics showing patterns, partner performance, specialty distribution, and patient flow across the network.</p></div></div></div>
                                )}
                                {activeTab === 'financial' && (
                                    <div className="sc-panel"><div className="sc-panel-body"><div className="sc-empty"><i className="bx bx-dollar-circle"></i><h3>Financial Reports</h3><p>Revenue from partnerships, inter-facility billing summaries, and collaboration-driven revenue trends.</p></div></div></div>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <DashboardFooter />
            </div>
            <Canvas />
        </>
    );
};

export default ReportsInsights;
